import classNames from 'classnames';
import { FC } from 'react';

import { IcClose16Icon } from '@gem/icons';
import { IconButton, IconViewSizes } from '@gem/ui-kit';

import { ChatBackgroundImageSize, ChatBackgroundType } from 'APP/constants/appearance';
import { ChatBackgroundImage } from 'APP/main/components/ChatBackground/ChatBackgroundImage/ChatBackgroundImage';
import { ChatBackgroundPattern } from 'APP/main/components/ChatBackground/ChatBackgroundPattern/ChatBackgroundPattern';
import { getChatBackgroundClassName } from 'APP/main/components/ChatBackground/hooks/useChatBackgroundClassName';
import { TChatBackground } from 'APP/store/Appearance/Appearance.types';
import chatBackgroundStyles from 'MAIN/components/ChatBackground/ChatBackground.styles.m.css';

import { useBackgroundItemPresenter } from './BackgroundItem.presenter';
import styles from './BackgroundItem.styles.m.css';

interface IBackgroundItemProps {
  background: TChatBackground;
  onChatBackgroundChange(chatBackground: TChatBackground): void;
  onDeleteCustomBackground(id: string): void;
  className?: string;
  isActive?: boolean;
}

const MASK_POSITION = '116px 112px';
const MASK_SIZE = '180%';

export const BackgroundItem: FC<IBackgroundItemProps> = ({
  background,
  onChatBackgroundChange,
  onDeleteCustomBackground,
  className,
  isActive = false,
}) => {
  const presenter = useBackgroundItemPresenter({ background, onDelete: onDeleteCustomBackground });
  const backgroundClassName =
    background.type === ChatBackgroundType.Pattern && !!presenter.pattern
      ? chatBackgroundStyles[getChatBackgroundClassName(background.background)]
      : '';

  return (
    <div
      role="button"
      tabIndex={0}
      className={classNames(className, styles.container, backgroundClassName, {
        [styles.active]: isActive,
      })}
      onClick={(): void => onChatBackgroundChange(background)}
    >
      <div className={styles.overlay}>
        <div className={styles.border}>
          <div className={styles.shadow} />
        </div>
      </div>
      {presenter.pattern && (
        <ChatBackgroundPattern
          pattern={presenter.pattern}
          maskPosition={MASK_POSITION}
          maskSize={MASK_SIZE}
          className={styles.background}
        />
      )}
      {!presenter.pattern && (
        <>
          <ChatBackgroundImage
            background={background}
            className={styles.background}
            size={ChatBackgroundImageSize.Miniature}
          />
          {background.type === ChatBackgroundType.Custom && (
            <IconButton
              Icon={IcClose16Icon}
              onClick={presenter.onDelete}
              className={styles.remove}
              iconClassName={styles.removeIcon}
              theme="12"
              size={IconViewSizes.Small}
            />
          )}
        </>
      )}
    </div>
  );
};
